import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  addFromWorkspaceAccessRequest,
  dismissWorkspaceAccessRequest,
  getListWorkspaceAccessRequestsQueryKey,
  getListWorkspaceMembersQueryKey,
  getReadMyWorkspaceAccessRequestQueryKey,
  getReadWorkspaceAccessLogQueryKey,
  requestWorkspaceAccess,
  useListWorkspaceAccessRequests,
  useReadMyWorkspaceAccessRequest,
  type readMyWorkspaceAccessRequestResponse,
} from '@/lib/api/generated/api';
import type { WorkspaceRole } from '@/lib/api/generated/model';

import { workspaceKeys } from '../queryKeys';

/**
 * Asking to join a workspace, and answering (spec 190, service spec 156).
 *
 * Here rather than beside the members list because three places use them — the
 * no-access page, the view-only chip, and the inbox's Add and Dismiss — and the
 * inbox may import this feature but not Settings.
 */

/** What a refusal means, in words: the service answers with a code. */
const REFUSALS: Record<string, string> = {
  ACCESS_ALREADY_GRANTED: 'You are already in this workspace.',
  ACCESS_REQUEST_NOT_FOUND: 'This request was answered already.',
  MEMBER_ALREADY_EXISTS: 'This person is already in the workspace.',
  MEMBER_NOT_IN_ORGANIZATION:
    'This person is not an organization member and cannot be added.',
  MEMBER_VERIFICATION_FAILED:
    'Organization membership could not be verified. Try again in a moment.',
  WORKSPACE_OWNERS_ONLY: 'Only this workspace’s owners can do this.',
};

function check(
  response: { status: number; data: unknown },
  expected: number,
  fallback: string,
): void {
  if (response.status === expected) return;
  const data = response.data;
  const code =
    data !== null && typeof data === 'object' && 'error' in data
      ? String(data.error)
      : '';
  throw new Error(REFUSALS[code] ?? fallback);
}

/** When the caller asked to join, or `null` when they have no open request. */
export function useMyAccessRequest(workspaceId: string) {
  const query = useReadMyWorkspaceAccessRequest(workspaceId);
  const response = query.data;
  return {
    requestedAt:
      response?.status === 200 ? (response.data.requested_at ?? null) : null,
    isPending: query.isPending,
  };
}

export function useRequestAccess(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation<readMyWorkspaceAccessRequestResponse, Error, void>({
    mutationFn: async () => {
      const response = await requestWorkspaceAccess(workspaceId);
      check(
        response,
        201,
        'The request could not be sent. Try again in a moment.',
      );
      // `check` threw on anything else; this only narrows the type.
      if (response.status !== 201) throw new Error('The request was not sent.');
      return { data: response.data, status: 200, headers: response.headers };
    },
    onSuccess: (sent) => {
      // The sent request is what "mine" now reads: written straight in, so the
      // band, its popover and the no-access page all say "sent" at once, with
      // no round trip in which Request access is offered again (spec 196).
      queryClient.setQueryData(
        getReadMyWorkspaceAccessRequestQueryKey(workspaceId),
        sent,
      );
    },
  });
}

/** The open requests, for an owner; not asked for unless `enabled`. */
export function useAccessRequests(workspaceId: string, enabled: boolean) {
  const query = useListWorkspaceAccessRequests(workspaceId, {
    query: { enabled },
  });
  const response = query.data;
  return {
    items: response?.status === 200 ? response.data.items : [],
    isPending: query.isPending && enabled,
  };
}

/** After an answer: the requests, the members, the workspace itself and its log. */
function useRefresh() {
  const queryClient = useQueryClient();
  return (workspaceId: string) => {
    void queryClient.invalidateQueries({
      queryKey: getReadWorkspaceAccessLogQueryKey(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: getListWorkspaceAccessRequestsQueryKey(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: getListWorkspaceMembersQueryKey(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.detail(workspaceId),
    });
  };
}

export function useAddFromRequest() {
  const refresh = useRefresh();
  return useMutation<
    void,
    Error,
    { workspaceId: string; requestId: string; role?: WorkspaceRole }
  >({
    mutationFn: async ({ workspaceId, requestId, role }) => {
      check(
        await addFromWorkspaceAccessRequest(workspaceId, requestId, {
          role: role ?? 'member',
        }),
        201,
        'This person could not be added.',
      );
    },
    onSuccess: (_result, { workspaceId }) => {
      refresh(workspaceId);
    },
  });
}

export function useDismissRequest() {
  const refresh = useRefresh();
  return useMutation<void, Error, { workspaceId: string; requestId: string }>({
    mutationFn: async ({ workspaceId, requestId }) => {
      check(
        await dismissWorkspaceAccessRequest(workspaceId, requestId),
        204,
        'The request could not be dismissed.',
      );
    },
    onSuccess: (_result, { workspaceId }) => {
      refresh(workspaceId);
    },
  });
}
